import {
  DownloadOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  MinusCircleOutlined,
  MinusOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  ToTopOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import {Button, Popconfirm, Slider, Tag, Tooltip} from 'antd'
import React, {useCallback} from 'react'
import {ToolType} from './toolLayers/CanvasToolManager'
import {useIntl} from 'react-intl'
import {NodeConfig} from 'konva/lib/Node'
import './PlantControlRecord.less'
import cx from 'classnames'

export type PlantControlRecordProps = {
  plantName: string
  plantId: number
  isVisible: boolean
  isSingle: boolean
  isSpecimen: boolean
  isPlanted: boolean
  isSinglePlanted: boolean
  isFromGardenPlan?: boolean
  isLocked?: boolean
  setVisibility: (isVisible: boolean) => void
  openPlantById: (id: number) => void
  setTool: (tool: ToolType) => void
  digUpPlant: (diggingPlantId?: number, diggingPlantSpecimenNumber?: number) => void
  plantPlant: (plantingPlantId?: number, plantingPlantSpecimenNumber?: number) => void
  specimenNumber: number
  addPlantSpecimen?: (plantId: number, newSpecimenCoordinates?: NodeConfig) => void
  removePlantSpecimen?: (plantId: number, specimenNumber: number) => void
}

export function PlantControlRecord(props: PlantControlRecordProps) {
  const intl = useIntl()
  return (
    <div className="plant-control-record">
      <span
        className={cx(
          'plant-control-record__name',
          !props.isSpecimen && !props.isSingle && 'plant-control-record__name--category',
          props.isFromGardenPlan && 'plant-control-record__name--garden-plan'
        )}
      >
        {props.plantName}
        {props.isFromGardenPlan && !props.isSpecimen && (
          <Tooltip placement="bottom" title={intl.formatMessage({id: 'plantFromGardenPlan'})}>
            <Tag className="plant-control-record__garden-plan-tag" color="gold">
              {intl.formatMessage({id: 'gardenPlanTag'})}
            </Tag>
          </Tooltip>
        )}
      </span>
      <span className="plant-control-record__description">
        {!props.isSpecimen && (
          <Tooltip placement="bottom" title={intl.formatMessage({id: 'openPlantDescription'})}>
            <Button
              size="small"
              shape="circle"
              icon={<EditOutlined />}
              type="link"
              onClick={() => props.openPlantById(props.plantId)}
            ></Button>
          </Tooltip>
        )}

        {!props.isLocked && props.isPlanted && (props.isSpecimen || props.isSingle) && (
          <Tooltip placement="bottom" title={intl.formatMessage({id: 'digUpPlant'})}>
            {props.isSinglePlanted ? (
              <Popconfirm
                placement="bottom"
                title={intl.formatMessage({
                  id: 'thisIsTheOnlyPlantedSpecimenOfThisPlant',
                })}
                description={intl.formatMessage({
                  id: 'afterDiggingThePlantWillBeTransferredToTheUnplantedOnes',
                })}
                icon={<QuestionCircleOutlined style={{color: 'red'}} />}
                onConfirm={async (e) => {
                  e.stopPropagation()
                  props.digUpPlant(props.plantId, props.specimenNumber)
                }}
                okText={intl.formatMessage({id: 'continue'})}
                cancelText={intl.formatMessage({id: 'cancel'})}
              >
                <ToTopOutlined />
              </Popconfirm>
            ) : (
              <Button
                size="small"
                shape="circle"
                icon={<ToTopOutlined />}
                type="link"
                onClick={(e) => {
                  e.stopPropagation()
                  props.digUpPlant(props.plantId, props.specimenNumber)
                }}
              ></Button>
            )}
          </Tooltip>
        )}
        {!props.isLocked && props.isPlanted && !props.isSpecimen && !props.isSingle && (
          <Tooltip placement="bottom" title={intl.formatMessage({id: 'digUpAllPlantSpecimens'})}>
            <Popconfirm
              placement="bottom"
              title={intl.formatMessage({id: 'areYouSureYouWantToDigUpAllTheSpecimensOfThisPlant'})}
              description={intl.formatMessage({
                id: 'afterDiggingThePlantWillBeTransferredToTheUnplantedOnes',
              })}
              icon={<QuestionCircleOutlined style={{color: 'red'}} />}
              onConfirm={async () => {
                props.digUpPlant(props.plantId)
                props.setTool(null)
              }}
              okText={intl.formatMessage({id: 'yes'})}
              cancelText={intl.formatMessage({id: 'no'})}
            >
              <UploadOutlined />
            </Popconfirm>
          </Tooltip>
        )}
        {!props.isLocked && !props.isPlanted && (props.isSingle || props.isSpecimen) && (
          <Tooltip placement="bottom" title={intl.formatMessage({id: 'plantPlant'})}>
            <Button
              size="small"
              shape="circle"
              icon={<DownloadOutlined />}
              type="link"
              onClick={(e) => {
                e.stopPropagation()
                props.plantPlant(props.plantId, props.specimenNumber)
                //props.setTool('plant')
              }}
            ></Button>
          </Tooltip>
        )}
        {!props.isLocked && !props.isSpecimen && (
          <Tooltip placement="bottom" title={intl.formatMessage({id: 'addPlantSpecimen'})}>
            <Button
              size="small"
              shape="circle"
              icon={<PlusOutlined />}
              type="link"
              onClick={() => {
                props.addPlantSpecimen(props.plantId)
              }}
            ></Button>
          </Tooltip>
        )}
        {!props.isLocked && props.isSpecimen && (
          <Tooltip placement="bottom" title={intl.formatMessage({id: 'removePlantSpecimen'})}>
            {props.isSinglePlanted ? (
              <Popconfirm
                placement="bottom"
                title={intl.formatMessage({id: 'thisIsTheOnlyPlantedSpecimenOfThisPlant'})}
                description={intl.formatMessage({
                  id: 'afterRemovingThePlantWillBeTransferredToTheUnplantedOnes',
                })}
                icon={<QuestionCircleOutlined style={{color: 'red'}} />}
                onConfirm={async () => {
                  props.removePlantSpecimen(props.plantId, props.specimenNumber)
                }}
                okText={intl.formatMessage({id: 'yes'})}
                cancelText={intl.formatMessage({id: 'no'})}
              >
                <MinusOutlined />
              </Popconfirm>
            ) : (
              <Button
                size="small"
                shape="circle"
                icon={<MinusOutlined />}
                type="link"
                onClick={() => {
                  props.removePlantSpecimen(props.plantId, props.specimenNumber)
                }}
              ></Button>
            )}
          </Tooltip>
        )}
        {!props.isSpecimen && props.isPlanted && (
          <Tooltip
            placement="bottom"
            title={
              props.isVisible
                ? intl.formatMessage({id: 'hidePlantOnSitePlan'})
                : intl.formatMessage({id: 'showPlantOnSitePlan'})
            }
          >
            <Button
              size="small"
              shape="circle"
              icon={props.isVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
              type="link"
              onClick={(e) => {
                props.setVisibility(!props.isVisible)
                e.stopPropagation()
              }}
            ></Button>
          </Tooltip>
        )}
      </span>
    </div>
  )
}
